import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { quickBooksReservedKeys } from 'app/model/linkedservice/quick-books.linkedservice';
import { optionsTrueFalse } from 'app/registries/linked-service/linked-service-shared-fields';
import { GlobalVariables } from 'app/services/globals';
import { format } from 'app/shared/utils/string.util';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { CommonResourceKind, DataSourceOptionKind } from '@powerquery/mashup-editor-client';

const commonControls: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'endpoint',
        label: stringResources.ModelName_Endpoint,
        required: true,
        placeholder: stringResources.QuickBooks_Endpoint_Placeholder,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'companyId',
        label: stringResources.ModelName_CompanyId,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'consumerKey',
        label: stringResources.ModelName_ClientId, // use client ID as display name
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'consumerSecret',
        label: stringResources.ModelName_ClientSecret, // use client secret as display name
        child: {
            type: 'password',
            label: stringResources.ModelName_ClientSecret,
            isLabelVertical: true,
            required: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.QuickBooks_DisplayText),
            stringResources.ModelName_ClientSecret)
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'accessToken',
        label: stringResources.ModelName_AccessToken,
        child: {
            type: 'password',
            label: stringResources.ModelName_AccessToken,
            isLabelVertical: true,
            required: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.QuickBooks_DisplayText),
            stringResources.ModelName_AccessToken),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        hide: ('{ !isOldResource }' as any) as boolean // it's an expression and will be evaluated into boolean-type value in control factory service
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'accessTokenSecret',
        label: stringResources.ModelName_AccessTokenSecret,
        child: {
            type: 'password',
            label: stringResources.ModelName_AccessTokenSecret,
            isLabelVertical: true,
            required: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.QuickBooks_DisplayText),
            stringResources.ModelName_AccessTokenSecret),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        hide: ('{ !isOldResource }' as any) as boolean // it's an expression and will be evaluated into boolean-type value in control factory service
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'refreshToken',
        label: stringResources.ModelName_RefreshToken,
        child: {
            type: 'password',
            label: stringResources.ModelName_RefreshToken,
            isLabelVertical: true,
            required: true,
            description: stringResources.RefreshToken_Description,
            descriptionLink: 'https://aka.ms/connector-quickbooks-linked-service'
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.QuickBooks_DisplayText),
            stringResources.ModelName_RefreshToken),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        hide: ('{ isOldResource }' as any) as boolean // it's an expression and will be evaluated into boolean-type value in control factory service
    }
];

const v1Controls: ControlMetadata[] = [
    ...commonControls,
    {
        type: 'radio',
        bind: 'useEncryptedEndpoints',
        label: stringResources.ModelName_UseEncryptedEndpoints,
        isLabelVertical: true,
        options: optionsTrueFalse,
        defaultValue: true
    },
    {
        type: 'key-val-list',
        bind: 'additionalFields',
        label: stringResources.AdditionalConnectionStringFields,
        hide: '{ isOldResource }',
        reservedKeys: quickBooksReservedKeys
    }
];

export const quickBooksMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.quickBooks.linkedServiceType,
    displayName: stringResources.QuickBooks_DisplayText,
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-quickbooks'
    },
    isFirstClass: true,
    supportDatasetV2: true,
    category: ['ServicesApps'],
    copySinkTypes: [],
    copySourceTypes: [adfModelTypeMap.quickBooks.copySourceType],
    isDeprecated: !GlobalVariables.showLegacy ? '{ version !== "v2" }' : false,
    showWarningMessage: '{ version !== "v2" }',
    supportedInTrident: GlobalVariables.tridentBatch1ConnectorPhase2,
    supportAutoFillCopilotInput: GlobalVariables.copilotConnectorsPhase2,
    isKnownInPipelineContext: GlobalVariables.copilotConnectorsPhase2,
    supportCopyJobSource: GlobalVariables.copyJobConnectorsPhase2,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: 'QuickBooksForPipeline' as CommonResourceKind
    },
    warningMessage: format(
        stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
        stringResources.VersionLabel.toLocaleLowerCase()
    ),
    controls: [
        {
            type: 'radio-switch',
            bind: 'version',
            label: stringResources.VersionLabel,
            isLabelVertical: true,
            required: true,
            options: driverVersionVersionOptions,
            defaultValue: driverVersions.v2,
            hide: GlobalVariables.showLegacy ? false : '{version === "v2" }',
            cases: [
                {
                    caseValue: driverVersions.v1,
                    children: [
                        {
                            type: 'hint',
                            hintMode: 'warning',
                            description: format(
                                stringResources.Connector_Legacy_IsNotSupportedWarning_alt6,
                                stringResources.VersionLabel.toLocaleLowerCase()),
                            link: 'https://learn.microsoft.com/azure/data-factory/connector-quickbooks'
                        },
                        ...v1Controls
                    ]
                },
                {
                    caseValue: driverVersions.v2,
                    children: commonControls

                }
            ]
        },
        ...commonControls.map((control) => {
            if (control.bind === 'accessTokenSecret' || control.bind === 'accessToken') {
                return {
                    ...control,
                    hide: GlobalVariables.showLegacy ? true : '{ !isOldResource || version !== "v2" }'
                };
            } else if (control.bind === 'refreshToken') {
                return {
                    ...control,
                    hide: GlobalVariables.showLegacy ? true : '{ isOldResource || version !== "v2" }'
                };
            }
            return {
                ...control,
                hide: !GlobalVariables.showLegacy ? '{version !== "v2" }' : true
            };
        })
    ]
};
